/**
 * What the piece page does with a copy of the piece it did not save through the autosave: a
 * claim's or the summary's response, or a refetch after a recheck, a photo or a social change.
 */
import { replaceEqualDeep } from '@tanstack/react-query'
import { isoAtLeast, type PieceField, type PieceFields } from './autosave'

export type AdoptAction =
  /** Already held, or older than what the page holds: leave everything as it is. */
  | { kind: 'ignore' }
  /** Move the autosave's base to this copy; put its body into the editor when the server's body changed. */
  | { kind: 'take'; setBody: boolean }
  /** A field with unsent edits changed on the server: stop saving and offer Reload. */
  | { kind: 'stale'; fields: PieceField[] }

/** The fields the page edits, as a copy of the piece holds them. The credit reads the caption as it
 *  will publish, which is the override whenever one is set. */
export function pieceFields(d: {
  title?: string | null; byline?: string | null; meta_description?: string | null; seo_title?: string | null; slug?: string | null
  featured_caption?: string | null; destination?: string | null; public_summary?: string | null; body_html?: string | null
}): PieceFields {
  const v = (x: string | null | undefined) => x ?? ''
  return {
    title: v(d.title), byline: v(d.byline), meta_description: v(d.meta_description), seo_title: v(d.seo_title), slug: v(d.slug),
    featured_caption_override: v(d.featured_caption), destination: (d.destination ?? 'section') as PieceFields['destination'],
    public_summary: v(d.public_summary), body_html: v(d.body_html),
  }
}

export function adoptDetail(
  current: {
    /** The autosave's base: the updated_at of the copy the page's edits apply to. */
    base: string | null
    /** That copy's field values: what the unsent edits were based on. */
    held: PieceFields
    /** The fields with edits typed but not saved (waiting, being sent or refused). */
    unsent: PieceField[]
    /** Their values: the one being sent and the newest one waiting. A copy that already holds one
     *  of them is our own edit coming back, not a clash. */
    values?: { inflight: PieceFields; queued: PieceFields }
  },
  incoming: { updated_at: string } & PieceFields,
): AdoptAction {
  if (current.base && isoAtLeast(current.base, incoming.updated_at)) return { kind: 'ignore' }
  const v = (x: string | undefined | null) => x ?? ''
  const changed = (f: PieceField) => v(incoming[f]) !== v(current.held[f])
  const ours = (f: PieceField) => {
    const vals = current.values
    if (!vals) return false
    return (f in vals.inflight && v(vals.inflight[f]) === v(incoming[f]))
      || (f in vals.queued && v(vals.queued[f]) === v(incoming[f]))
  }
  const clashes = current.unsent.filter((f) => changed(f) && !ours(f))
  if (clashes.length) return { kind: 'stale', fields: clashes }
  // A body with edits of its own here stays as the editor has it.
  return { kind: 'take', setBody: changed('body_html') && !current.unsent.includes('body_html') }
}

/** The query cache's structural sharing for a piece: a copy older than the one held never replaces
 *  it, so a refetch that started before a save cannot land after it and undo it. */
export function keepNewest(oldData: unknown, newData: unknown): unknown {
  const at = (d: unknown) => (d && typeof d === 'object' ? (d as { updated_at?: unknown }).updated_at : undefined)
  const o = at(oldData)
  const n = at(newData)
  if (typeof o === 'string' && typeof n === 'string' && !isoAtLeast(n, o)) return oldData
  return replaceEqualDeep(oldData, newData)
}
